import { Field } from 'formik';
import {
  Dropdown,
  Icon,
  Popup,
  Message,
  Form as SemanticForm
} from 'semantic-ui-react';

import '../../styles/bookWorksheet.css';


// Custom Input component with error popups
export const CustomInput = ({ label, error, fluid, ...props }) => {
  // Extract fluid prop and use it for styling
  const inputStyle = {
    width: fluid ? '100%' : 'auto',
    padding: '0.67857143em 1em',
    border: error ? '1px solid #e0b4b4' : '1px solid rgba(34,36,38,.15)',
    borderRadius: '.28571429rem',
    transition: 'box-shadow .1s ease,border-color .1s ease',
    backgroundColor: error ? '#fff6f6' : 'transparent',
    ...props.style
  };

  return (
    <div style={{ marginBottom: '1em' }}>
      {label && (
        <label style={{ display: 'block', marginBottom: '0.1em' }}>
          {label}
          {error && (
            <Popup
              content={error}
              trigger={
                <Icon
                  name="exclamation circle"
                  color="red"
                  style={{ marginLeft: '0.5em', cursor: 'pointer' }}
                />
              }
              position="top center"
              size="tiny"
            />
          )}
        </label>
      )}
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center'
      }}>
        <input
          {...props}
          style={inputStyle}
        // fluid is not passed to input element
        />
      </div>
    </div>
  );
};

// Formik wrapper for CustomInput
export const FormikInput = ({ field, form, label, ...props }) => {
  const hasError = form.touched[field.name] && form.errors[field.name];

  return (
    <CustomInput
      {...field}
      {...props}
      label={label}
      error={hasError ? form.errors[field.name] : null}
      onChange={(e) => form.setFieldValue(field.name, e.target.value)}
      onBlur={() => form.setFieldTouched(field.name, true)}
    />
  );
};

// Custom TextArea component with error popups
export const CustomTextArea = ({ label, error, fluid, rows = 3, ...props }) => {
  // Extract fluid and rows props and use them for styling
  const textareaStyle = {
    width: '100%', // Always use 100% instead of conditional width
    padding: '0.67857143em 1em',
    border: error ? '1px solid #e0b4b4' : '1px solid rgba(34,36,38,.15)',
    borderRadius: '.28571429rem',
    transition: 'box-shadow .1s ease,border-color .1s ease',
    backgroundColor: error ? '#fff6f6' : 'transparent',
    minHeight: `${rows * 2.5}em`,
    resize: 'vertical',
    boxSizing: 'border-box', // This ensures padding is included in width calculation
    ...props.style
  };

  return (
    // <div style={{ marginBottom: '1em' }}>
    <div >

      {label && (
        <label style={{ display: 'block', marginBottom: '0.5em' }}>
          {label}
          {error && (
            <Popup
              content={error}
              trigger={
                <Icon
                  name="exclamation circle"
                  color="red"
                  style={{ marginLeft: '0.5em', cursor: 'pointer' }}
                />
              }
              position="top center"
              size="tiny"
            />
          )}
        </label>
      )}
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        width: '100%' // Ensure the container takes full width
      }}>
        <textarea
          {...props}
          style={textareaStyle}
          rows={rows}
        />
        {error && (
          <Popup
            content={error}
            trigger={
              <Icon
                name="exclamation circle"
                color="red"
                style={{
                  position: 'absolute',
                  right: '0.5em',
                  top: '0.5em',
                  pointerEvents: 'auto',
                  cursor: 'pointer'
                }}
              />
            }
            position="top right"
            size="tiny"
          />
        )}
      </div>
    </div>
  );
};

// Formik wrapper for CustomTextArea
export const FormikTextArea = ({ field, form, label, ...props }) => {
  const hasError = form.touched[field.name] && form.errors[field.name];
  return (
    <CustomTextArea
      {...field}
      {...props}
      label={label}
      error={hasError ? form.errors[field.name] : null}
      onChange={(e) => form.setFieldValue(field.name, e.target.value)}
      onBlur={() => form.setFieldTouched(field.name, true)}
    />
  );
};

// Formik Select component
export const FormikSelect = ({ field, form, options, label, placeholder, ...props }) => (
  <div className="transparent-dropdown" style={{
    marginTop: '0.0em',  // Maintain spacing similar to a form field
    position: 'relative',  // Create a new stacking context
    zIndex: '6000 !important'
  }}>
    {label && <label className='labelLaf'>{label}</label>}
    <Dropdown
      selection
      options={options}
      value={field.value}
      placeholder={placeholder}
      onChange={(e, { value }) => form.setFieldValue(field.name, value)}
      onBlur={() => form.setFieldTouched(field.name, true)}
      error={form.errors[field.name] && form.touched[field.name]}
      fluid
      {...props}
    />
  </div>
);

// Custom Checkbox component with error popups
export const CustomCheckbox = ({ label, error, checked, ...props }) => {
  return (
    <div style={{ marginBottom: '1em', position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div
          style={{
            position: 'relative',
            width: '20px',
            height: '20px',
            marginRight: '0.5em',
            border: '1px solid #333',
            backgroundColor: 'white',
            borderRadius: '3px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}
        >
          <input
            type="checkbox"
            {...props}
            style={{
              position: 'absolute',
              opacity: 0,
              width: '100%',
              height: '100%',
              cursor: 'pointer',
              zIndex: 2,
              ...props.style
            }}
          />
          {checked && (
            <Icon
              name="check"
              style={{
                color: '#2185d0',
                fontSize: '16px',
                position: 'absolute',
                zIndex: 1
              }}
            />
          )}
        </div>
        <label style={{ margin: 0, cursor: 'pointer', fontWeight: 'bold' }}>
          {label}
          {error && (
            <Popup
              content={error}
              trigger={
                <Icon
                  name="exclamation circle"
                  color="red"
                  style={{ marginLeft: '0.5em', cursor: 'pointer' }}
                />
              }
              position="top center"
              size="tiny"
            />
          )}
        </label>
      </div>
    </div>
  );
};

// Formik wrapper for CustomCheckbox
export const FormikCheckbox = ({ field, form, label, ...props }) => {
  const hasError = form.touched[field.name] && form.errors[field.name];

  return (
    <CustomCheckbox
      {...field}
      {...props}
      label={label}
      error={hasError ? form.errors[field.name] : null}
      checked={field.value}
      onChange={(e) => form.setFieldValue(field.name, e.target.checked)}
      onBlur={() => form.setFieldTouched(field.name, true)}
    />
  );
};

// Export a component that includes all Formik fields
export const FormikField = (props) => {
  const { component, ...rest } = props;
  return (
    <Field
      component={component}
      {...rest}
    />
  );
};

export default {
  CustomInput,
  FormikInput,
  CustomTextArea,
  FormikTextArea,
  FormikSelect,
  CustomCheckbox,
  FormikCheckbox,
  FormikField
};
